import { ClearOutlined } from "@ant-design/icons";
import { Button, Form, Space, Spin } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { classNames } from "src/common/classNames";
import { SettingsIconStd } from "src/common/icons";
import { ZObjState } from "src/types/ZObjState";
import { AttsHierarchy } from "./AttsHierarchy/AttsHierarchy";
import { AttsTree } from "./AttsTree/AttsTree";
import { AttsTreeSelectedPath } from "./AttsTree/AttsTreeSelectedPath";
import { EntityFiltersPageStore } from "./EntityFiltersPageStore";
import styles from "./EntityFiltersTable.module.less";
import { EntityCount } from "./EntityList/EntityCount";
import { EntityList } from "./EntityList/EntityList";
import { SlideMenuAttrList } from "./SlideMenu/SlideMenuAttrList";
import { StateFilter } from "./StateFilter";

type MenuItemKey = "atts";

type PropsEntityFiltersTable = {
  store: EntityFiltersPageStore;
  statesLoader?: () => Promise<ZObjState[]>;
};
export const EntityFiltersTable: React.FC<PropsEntityFiltersTable> = observer(
  ({ store, statesLoader }) => {
    const [menuKey, setMenuKey] = React.useState<MenuItemKey | null>(null);
    const openAttsMenu = () => setMenuKey("atts");
    const resetFilters = () => {
      store.resetFilters();
    };
    const closeMenu = () => setMenuKey(null);
    const objectId = store.currObjId;

    React.useEffect(() => {
      if (!objectId) return;
      store.init(objectId, { statesLoader });
    }, [objectId, store, store.filterRestrictions, statesLoader]);

    React.useEffect(() => {
      store.setSiderVisible(false);
    }, [store]);

    if (!objectId) return null;

    return (
      <div className={classNames([styles.lWSider, styles.pageInner])}>
        <div
          className={classNames([
            styles.sider,
            [!store.siderVisible, styles.hidden],
          ])}
        >
          {store.tableStore && <EntityCount store={store.tableStore} />}

          <div className={styles.siderHeader}>
            <div className={styles.headerButtons}>
              <Button
                type="primary"
                size="large"
                onClick={() => openAttsMenu()}
                icon={<SettingsIconStd />}
              >
                {t("Filter")}
              </Button>
              <Button
                size="large"
                onClick={() => resetFilters()}
                icon={<ClearOutlined />}
              >
                {t("Reset")}
              </Button>
            </div>
          </div>
          <div className={styles.siderContent}>
            <div className={styles.siderBox}>
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Form.Item label={t("State")}>
                  <StateFilter
                    key={objectId}
                    value={store.tableStore?.filters?.stateIds ?? []}
                    options={store.objectStatesOptions}
                    disabled={!store.tableStore || store.loading}
                    onApply={(value) => store.applyStateFilter(value)}
                  />
                </Form.Item>

                <AttsHierarchy store={store} />
                <AttsTreeSelectedPath store={store} objectId={objectId} />
                <AttsTree store={store} />
              </Space>
            </div>
          </div>
          <SlideMenuAttrList
            onClose={closeMenu}
            open={menuKey === "atts"}
            store={store}
          />
        </div>
        <div className={styles.lWSiderMainContent}>
          {store.loading ? (
            <div className={styles.loaderBox}>
              <Spin spinning={store.loading} />
            </div>
          ) : (
            <EntityList
              store={store}
              onRowClick={(row, index) => {
                store.onRowClick?.(row, index);
              }}
            />
          )}
        </div>
      </div>
    );
  },
);
